import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { addNote, clearAddNoteError, deleteNote, fetchTopLevelNotes, openToPath } from "./workspaceNoteSlice"
import {ensureWorkspaceTemplate} from "../utils/reduxUtilities"

export const NOTE_ACTIONS = {
	scrollToNote: "scroll-to-note",
	addChild: "add-child",
	addRoot: "add-root",
	addSibling: "add-sibling",
	editNote: "edit",
	cancelNote: "cancel-note"
}

export const scrollToNote = createAsyncThunk(
	"noteAction/scrollToNote",
	async ({action, noteId, path, note, showLoading}, {rejectWithValue, dispatch}) => {
		try {
			// dispatch note expansion action
			// await and unwrap to ensure fulfilled state is complete
			const {workspaceId} = note
			await dispatch(openToPath({path, note})).unwrap()
			return {action, noteId, path, showLoading, workspaceId}
		} catch (error) {
			return rejectWithValue(error?.response?.message || "Error restoring note")
		}
	}
)
const initialValue = {
	noteId: null,
	action: null,
	showLoading: false
}
const ensureWorkspace = (state, workspaceId) => ensureWorkspaceTemplate(state, workspaceId, initialValue)

export const noteActionSlice = createSlice({
	name: "noteAction",
	initialState: {
		byWorkspace: { },
	},
	reducers: {
		setNoteAction: (state, action) => {
			const {workspaceId, noteId, action: noteAction} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			workspace.noteId = noteId
			workspace.action = noteAction
			workspace.showLoading = false
		},
		clearNoteAction: (state, action) => {
			const workspace = ensureWorkspace(state, action.payload)
			workspace.noteId = initialValue.noteId
			workspace.action = initialValue.action
			workspace.showLoading = false
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(
				scrollToNote.fulfilled, 
				noteActionSlice.caseReducers.setNoteAction
			)
			.addCase(
				scrollToNote.pending,
				(state, action) => {
					const {showLoading, note} = action.meta.arg
					const {workspaceId} = note
					const workspace = ensureWorkspace(state, workspaceId)
					if (showLoading) {
						workspace.showLoading = showLoading
					}
				}
			)
			.addCase(fetchTopLevelNotes.fulfilled, (state, action) => {
				const workspaceId = (action.meta.arg)
				// Failsafe to ensure that an empty workspace doesn't hit a bugged state (no notes = force <AddNote> open)
				if (Object.keys(action.payload).length === 0) {
					const workspace = ensureWorkspace(state, workspaceId)
					workspace.noteId = null
					workspace.action = NOTE_ACTIONS.addRoot
					workspace.showLoading = false
				}
			})
			// Failsafe to ensure that an empty workspace doesn't hit a bugged state (no notes = force <AddNote> open)
			// note: this could be redundant, as deleteNote calls fetchTopLevelNotes, which should trigger above ER
			.addCase(deleteNote.fulfilled, (state, action) => {
				const { isTopLevelEmpty, workspaceId } = action.payload
				if (isTopLevelEmpty) {
					const workspace = ensureWorkspace(state, workspaceId)
					workspace.noteId = initialValue.noteId
					workspace.action = NOTE_ACTIONS.addRoot
				}
			})
			.addCase(
				addNote.fulfilled,
				(state, action) => {
					const {workspaceId} = action.payload
					noteActionSlice.caseReducers.clearNoteAction(state, {payload: workspaceId})
				}
			)
			.addCase(clearAddNoteError, (state, action) => {
				const {workspaceId} = action.payload
				noteActionSlice.caseReducers.clearNoteAction(state, {payload: workspaceId})
			})
	}
})

export const { setNoteAction, clearNoteAction } = noteActionSlice.actions

export const selectNoteAction = (state, workspaceId) => state.noteAction.byWorkspace[workspaceId] ?? initialValue

export const selectShowNoteActionLoading = (state, workspaceId) => !!state.noteAction.byWorkspace[workspaceId]?.showLoading ?? initialValue.showLoading
export default noteActionSlice.reducer
